/**
 * JiraConnectionForm — "Add Jira Connection".
 *
 * Registers a Jira connection (instance + credential) so LeftShield can create/sync issues.
 * `POST` via `useCreateJiraConnection()`.
 *
 * Pattern: `react-hook-form` + `zodResolver`, schema imported from `JiraConnection.schema.ts`
 * (never redefined), Fluent `Field` for verbatim validation messages, server errors mapped via
 * the shared `applyServerErrors` (Charter M3 / form-standard.context.md). The outer `Dialog`
 * chrome is owned by `IntegrationsPage.tsx` (this feature's own `components/`, not `src/pages/**`)
 * — this component renders the form CONTENT only and keeps its existing `onSuccess`/`onCancel`
 * prop contract so the hosting page needs no change.
 */
import type { FC } from 'react';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Field, Input, Select, makeStyles, tokens } from '@fluentui/react-components';
import { PlugConnectedAddRegular } from '@fluentui/react-icons';
import { useCreateJiraConnection } from '../hooks';
import type { CreateJiraConnectionRequest } from '../types';
import { applyServerErrors, useReportFormDirty, FormActions, ServerErrorBanner } from '../../../components/forms';
import { JIRA_AUTH_TYPE_OPTIONS, JIRA_DEPLOYMENT_TYPE_OPTIONS } from './connectionFormFields';
import {
  JiraConnectionSchema,
  type JiraConnectionFormValues,
  type JiraConnectionFormOutput,
} from './JiraConnection.schema';

const useStyles = makeStyles({
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  row: {
    display: 'flex',
    gap: tokens.spacingHorizontalM,
    '& > *': {
      flex: 1,
    },
  },
});

// Known request-DTO field keys — used by the server-error -> field mapping.
const KNOWN_FIELDS = ['name', 'instanceUrl', 'deploymentType', 'authType', 'username', 'apiToken'] as const;

const DEFAULT_VALUES: JiraConnectionFormValues = {
  name: '',
  instanceUrl: '',
  deploymentType: 'Cloud',
  authType: 'ApiToken',
  username: '',
  apiToken: '',
};

export interface JiraConnectionFormProps {
  onSuccess?: () => void;
  onCancel?: () => void;
  /** Reports dirty state up so the hosting dialog can guard an unsaved-changes dismiss. */
  onDirtyChange?: (dirty: boolean) => void;
}

export const JiraConnectionForm: FC<JiraConnectionFormProps> = ({ onSuccess, onCancel, onDirtyChange }) => {
  const styles = useStyles();

  const {
    control,
    handleSubmit,
    setError,
    reset,
    formState: { errors, isDirty },
  } = useForm<JiraConnectionFormValues, unknown, JiraConnectionFormOutput>({
    resolver: zodResolver(JiraConnectionSchema),
    mode: 'onTouched',
    defaultValues: DEFAULT_VALUES,
  });

  useReportFormDirty(isDirty, onDirtyChange);

  const [globalError, setGlobalError] = useState<string | undefined>();

  const createConnection = useCreateJiraConnection();

  const onValid = (values: JiraConnectionFormOutput) => {
    // Guard double-submit.
    if (createConnection.isPending) return;
    setGlobalError(undefined);

    const request: CreateJiraConnectionRequest = {
      name: values.name,
      instanceUrl: values.instanceUrl,
      deploymentType: values.deploymentType,
      authType: values.authType,
      username: values.username,
      apiToken: values.apiToken,
    };

    createConnection.mutate(request, {
      onSuccess: () => {
        reset(DEFAULT_VALUES);
        onSuccess?.();
      },
      onError: (error) => {
        const { globalMessage } = applyServerErrors(error, KNOWN_FIELDS, setError);
        setGlobalError(globalMessage);
      },
    });
  };

  return (
    <form onSubmit={handleSubmit(onValid)} className={styles.form} noValidate>
      <ServerErrorBanner title="Error" message={globalError} testId="jira-connection-form-server-error" />

      <Field
        label="Connection Name"
        required
        validationState={errors.name ? 'error' : undefined}
        validationMessage={errors.name?.message}
      >
        <Controller
          name="name"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              data-testid="jira-connection-form-name"
              value={field.value ?? ''}
              placeholder="My Jira Cloud"
              disabled={createConnection.isPending}
            />
          )}
        />
      </Field>

      <Field
        label="Instance URL"
        required
        validationState={errors.instanceUrl ? 'error' : undefined}
        validationMessage={errors.instanceUrl?.message}
        hint="e.g., https://your-domain.atlassian.net for Cloud"
      >
        <Controller
          name="instanceUrl"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              data-testid="jira-connection-form-instanceUrl"
              value={field.value ?? ''}
              placeholder="https://your-domain.atlassian.net"
              disabled={createConnection.isPending}
            />
          )}
        />
      </Field>

      <div className={styles.row}>
        <Field label="Deployment Type" required>
          <Controller
            name="deploymentType"
            control={control}
            render={({ field }) => (
              <Select
                value={field.value}
                onChange={(_, data) => field.onChange(data.value)}
                onBlur={field.onBlur}
                disabled={createConnection.isPending}
                data-testid="jira-connection-form-deploymentType"
              >
                {JIRA_DEPLOYMENT_TYPE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            )}
          />
        </Field>

        <Field label="Authentication Type" required>
          <Controller
            name="authType"
            control={control}
            render={({ field }) => (
              <Select
                value={field.value}
                onChange={(_, data) => field.onChange(data.value)}
                onBlur={field.onBlur}
                disabled={createConnection.isPending}
                data-testid="jira-connection-form-authType"
              >
                {JIRA_AUTH_TYPE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            )}
          />
        </Field>
      </div>

      <Field
        label="Username / Email"
        required
        validationState={errors.username ? 'error' : undefined}
        validationMessage={errors.username?.message}
        hint="For Cloud, use your Atlassian account email"
      >
        <Controller
          name="username"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              data-testid="jira-connection-form-username"
              value={field.value ?? ''}
              placeholder="user@example.com"
              disabled={createConnection.isPending}
            />
          )}
        />
      </Field>

      <Field
        label="API Token"
        required
        validationState={errors.apiToken ? 'error' : undefined}
        validationMessage={errors.apiToken?.message}
        hint="Generate an API token from your Atlassian account settings"
      >
        <Controller
          name="apiToken"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              type="password"
              data-testid="jira-connection-form-apiToken"
              value={field.value ?? ''}
              placeholder="Your API token"
              disabled={createConnection.isPending}
            />
          )}
        />
      </Field>

      <FormActions
        mode="create"
        testIdPrefix="jira-connection-form"
        isSubmitting={createConnection.isPending}
        onCancel={onCancel}
        submitLabel="Create Connection"
        submittingLabel="Creating…"
        submitIcon={<PlugConnectedAddRegular />}
      />
    </form>
  );
};
